import React, { useState } from 'react';
import { X, Save, Bookmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useFilterPresets } from '@/hooks/useFilterPresets';
import type { FilterState } from '@/types/filterTypes';

interface FilterPresetsProps {
  currentFilters: FilterState;
  onLoadPreset: (filters: FilterState) => void;
}

const FilterPresets: React.FC<FilterPresetsProps> = ({ currentFilters, onLoadPreset }) => {
  const { presets, savePreset, deletePreset } = useFilterPresets();
  const [saving, setSaving] = useState(false);
  const [presetName, setPresetName] = useState('');

  const handleSave = async () => {
    const name = presetName.trim();
    if (!name) return;
    await savePreset(name, currentFilters);
    setPresetName('');
    setSaving(false);
  };

  return (
    <div className="px-4 pb-3 border-b border-border">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
          <Bookmark className="h-3 w-3" />
          Saved Presets
        </span>
        {!saving && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => setSaving(true)}
          >
            <Save className="h-3 w-3 mr-1" />
            Save current
          </Button>
        )}
      </div>

      {saving && (
        <div className="flex items-center gap-2 mb-2">
          <Input
            value={presetName}
            onChange={e => setPresetName(e.target.value)}
            placeholder="Preset name..."
            className="h-7 text-xs flex-1"
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') setSaving(false);
            }}
          />
          <Button size="sm" className="h-7 px-2 text-xs" onClick={handleSave} disabled={!presetName.trim()}>
            Save
          </Button>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setSaving(false); setPresetName(''); }}>
            Cancel
          </Button>
        </div>
      )}

      {presets.length === 0 && !saving && (
        <p className="text-xs text-muted-foreground italic">No saved presets yet</p>
      )}

      <div className="flex flex-wrap gap-1">
        {presets.map(preset => (
          <div
            key={preset.id}
            className="flex items-center gap-1 bg-secondary text-secondary-foreground rounded-full px-2 py-0.5 text-xs cursor-pointer hover:bg-secondary/80 group"
          >
            <span onClick={() => onLoadPreset(preset.filters)} className="max-w-[120px] truncate">
              {preset.name}
            </span>
            <button
              onClick={e => { e.stopPropagation(); deletePreset(preset.id); }}
              className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 hover:text-destructive"
              aria-label={`Delete preset ${preset.name}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FilterPresets;
